import { useQuery } from '@tanstack/react-query';
import { Download, ExternalLink, Lock, Info } from 'lucide-react';
import { Button } from '@/components/ui/button.js';
import { Card } from '@/components/ui/card.js';
import { Badge } from '@/components/ui/badge.js';
import { customInstance } from '@edison/api-client-react';

function formatCurrency(n: number) {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: 0,
  }).format(n);
}

interface Schedule1Data {
  claimName?: string;
  incomeYear?: string;
  tenantName?: string;
  abn?: string | null;
  totalRdHours?: number;
  totalRdExpenditure?: number;
  lockedAt?: string;
  activities?: Array<{
    activityId: string;
    name: string;
    projectName?: string | null;
    rdHours: number;
    rdExpenditure: number;
  }>;
}

interface Props {
  claimId: string;
  claimStatus: string;
}

// TODO(ENG-052): same Safari blob-naming fix needed here as the reviewer-portal labour-timesheet/
// rate-card exports (fix/server-side-csv-export) — real Safari ignores this Blob's `a.download`
// filename and saves under the blob's UUID with no extension. Out of scope for that fix; tracked
// separately.
function exportCsv(data: Schedule1Data, claimId: string) {
  const rows = [
    ['Field', 'Value'],
    ['Registered R&D entity', data.tenantName ?? ''],
    ['ABN', data.abn ?? ''],
    ['Income year', data.incomeYear ?? ''],
    ['Core R&D activities', String(data.activities?.length ?? 0)],
    ['Total notional R&D deductions', String(data.totalRdExpenditure ?? 0)],
    ['Refundable offset (43.5%)', String(Math.round((data.totalRdExpenditure ?? 0) * 0.435))],
    ['Overseas findings', 'None'],
    [],
    ['Activity', 'Project', 'R&D Hours', 'Expenditure (AUD)'],
    ...(data.activities ?? []).map(a => [
      a.name,
      a.projectName ?? '',
      String(a.rdHours.toFixed(1)),
      String(a.rdExpenditure.toFixed(0)),
    ]),
  ];

  const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `schedule1-${claimId}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export function ClaimSchedule1Tab({ claimId, claimStatus }: Props) {
  const isLocked = claimStatus === 'locked';

  const { data, isLoading } = useQuery<Schedule1Data>({
    queryKey: ['claim-schedule1', claimId],
    enabled: isLocked,
    queryFn: async () => {
      const res = await customInstance<{ data: Schedule1Data }>({
        url: `/api/claims/${claimId}/schedule1`,
        method: 'GET',
      });
      return res.data;
    },
  });

  return (
    <div className="space-y-5">
      {/* Info banner */}
      <div className="flex items-start gap-3 rounded-xl border border-hairline bg-surface-2 px-4 py-3 text-[13px] text-dim">
        <Info className="h-4 w-4 shrink-0 mt-0.5 text-primary/60" aria-hidden="true" />
        <p>
          These are the data inputs for a registered tax agent — Edison prepares, your agent lodges.
          Generated from approved evidence only.
        </p>
      </div>

      {!isLocked && (
        <div className="rounded-xl border border-hairline bg-card p-5 space-y-3">
          <h2 className="font-semibold text-[15px]">ATO Schedule 1 — Part A inputs</h2>
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <Lock className="h-4 w-4 shrink-0" aria-hidden="true" />
            Lock the claim in the Readiness tab to generate ATO outputs.
          </div>
        </div>
      )}

      {isLocked && isLoading && (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-10 rounded-lg skeleton" />
          ))}
        </div>
      )}

      {isLocked && !isLoading && data && (
        <>
          <Card variant="static" className="overflow-hidden">
            {/* Card header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-hairline">
              <div>
                <h2 className="font-semibold text-[15px]">ATO Schedule 1 — Part A inputs</h2>
                <p className="text-[12px] text-dim mt-0.5">
                  Income year {data.incomeYear}
                  {data.lockedAt && ` · locked ${new Date(data.lockedAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}`}
                </p>
              </div>
              <Badge variant="positive">
                <Lock className="h-3 w-3 mr-1" aria-hidden="true" />
                Locked
              </Badge>
            </div>

            {/* Key-value rows */}
            <div className="divide-y divide-hairline">
              {[
                { label: 'Registered R&D entity', value: data.tenantName ?? '—' },
                { label: 'Income year', value: data.incomeYear ? `${data.incomeYear} (1 Jul ${parseInt(data.incomeYear.replace('FY', '')) - 1} – 30 Jun ${data.incomeYear.replace('FY', '')})` : '—' },
                { label: 'Core R&D activities', value: `${data.activities?.length ?? 0} activities across ${new Set(data.activities?.map(a => a.projectName)).size ?? 0} projects` },
                { label: 'Total notional R&D deductions', value: formatCurrency(data.totalRdExpenditure ?? 0), bold: true },
                { label: 'Refundable offset (43.5%)', value: formatCurrency((data.totalRdExpenditure ?? 0) * 0.435), bold: true },
                { label: 'Overseas findings', value: 'None' },
              ].map(({ label, value, bold }) => (
                <div key={label} className="flex items-center justify-between px-5 py-3 text-[13.5px]">
                  <span className="text-dim">{label}</span>
                  <span className={bold ? 'font-semibold text-ink' : 'text-ink'}>{value}</span>
                </div>
              ))}
            </div>

            {/* ABN row */}
            {data.abn && (
              <div className="flex items-center justify-between px-5 py-3 text-[13.5px] border-t border-hairline">
                <span className="text-dim">ABN</span>
                <span className="font-mono text-ink">{data.abn}</span>
              </div>
            )}
          </Card>

          {/* Downloads */}
          <div className="flex flex-wrap gap-3">
            <Button variant="secondary" onClick={() => exportCsv(data, claimId)}>
              <Download className="h-4 w-4" aria-hidden="true" />
              Export CSV
            </Button>
            <a href={`/api/claims/${claimId}/aus-industry`} target="_blank" rel="noopener noreferrer">
              <Button variant="secondary">
                <ExternalLink className="h-4 w-4" aria-hidden="true" />
                AusIndustry Registration Draft
              </Button>
            </a>
          </div>
        </>
      )}
    </div>
  );
}
